import { useCallback, useMemo, useState } from 'react';
import { selectContaDigitalProduct, useAppSelector, useContaDigital } from '@ibk/shared-config';
import { useRequiresAuthorization } from '@ibk/shared-permissioning';
import { useTranslation } from '@ibk/shared-translation';
import { PermissionsDomains, PermissionsResources } from '@ibk/shared-types';
export function usePixTransferValue() {
    const [value, setValue] = useState('');
    const [isValid, setIsValid] = useState(false);
    const [insufficientBalanceError, setInsufficientBalanceError] = useState(null);
    const { t } = useTranslation();
    const contaDigital = useAppSelector(selectContaDigitalProduct);
    const canSeeBalance = useRequiresAuthorization(PermissionsDomains.ACCOUNT, [
        PermissionsResources.ACCOUNT_VIEW_BALANCE,
    ]);
    const showBalance = useAppSelector(state => state.showBalance.showBalance);
    const { data: contaDigitalData, isLoading: balanceLoading } = useContaDigital({ enabled: !!canSeeBalance });
    const balance = useMemo(() => {
        const amount = typeof contaDigitalData?.balance === 'object' && contaDigitalData?.balance !== null
            ? (contaDigitalData.balance.amount ?? 0)
            : 0;
        return amount;
    }, [contaDigitalData]);
    const formattedBalance = useMemo(() => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(balance);
    }, [balance]);
    const getBalanceValue = () => {
        if (!showBalance)
            return '*****';
        if (balanceLoading)
            return undefined;
        return formattedBalance;
    };
    const formatCurrency = useCallback((inputValue) => {
        const numericValue = inputValue.replace(/\D/g, '');
        if (numericValue === '')
            return '';
        const number = parseInt(numericValue, 10) / 100;
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(number);
    }, []);
    const parseCurrency = useCallback((centavosValue) => {
        const numericValue = parseInt(centavosValue, 10);
        if (isNaN(numericValue))
            return 0;
        return numericValue / 100;
    }, []);
    const formattedValue = useMemo(() => {
        return value ? formatCurrency(value) : '';
    }, [value, formatCurrency]);
    const validateValue = useCallback((inputValue) => {
        const numericValue = inputValue.replace(/\D/g, '');
        if (numericValue === '')
            return false;
        const amount = parseInt(numericValue, 10) / 100;
        return amount > 0 && amount <= balance;
    }, [balance]);
    const handleValueChange = useCallback((inputValue) => {
        const numericValue = inputValue.replace(/\D/g, '');
        setValue(numericValue);
        const isValidValue = validateValue(numericValue);
        setIsValid(isValidValue);
        if (numericValue !== '') {
            const amount = parseInt(numericValue, 10) / 100;
            if (amount > 0 && amount > balance) {
                setInsufficientBalanceError(t('insufficient-balance-inline-error'));
            }
            else {
                setInsufficientBalanceError(null);
            }
        }
        else {
            setInsufficientBalanceError(null);
        }
    }, [validateValue, balance, t]);
    return {
        value,
        isValid,
        balance,
        isLoading: contaDigital?.isLoading ?? false,
        error: contaDigital?.error ?? null,
        formattedBalance,
        formattedValue,
        insufficientBalanceError,
        handleValueChange,
        validateValue,
        formatCurrency,
        parseCurrency,
        getBalanceValue,
    };
}
